import { useState, useRef, useMemo, DragEvent, ChangeEvent, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@mui/material';

import { ReactComponent as FrameIcon } from "@/assets/icons/Frame.svg";
import { FileDetails, Image } from '@/types/Image.type';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { generateUniqueId, getFileExtension, interpolate } from '@/utils/string.util';
import { supportedImageTypes, validateInspectionImageFile } from '@/utils/image.util';
import { toast } from 'react-toastify';
import { ImageCard } from '../ImageCard/ImageCard';

import styles from './Images.module.scss';

interface ImageProps {
    type: InspectionImageType
    images: Image[],
    inspectionId: string,
    onAdd: (file: FileDetails) => void,
    onRemove: (img: Image) => void;
    gallerySectionLabel?: string;
    requirePortraitOrientation?: boolean;
}

export const Images: React.FC<ImageProps> = ({ type, images, onAdd, onRemove, inspectionId, gallerySectionLabel, requirePortraitOrientation = false }: ImageProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    const defaultGalleryLabel = gallerySectionLabel?.trim() || t('Images');

    const allInspectionImageGroups = useMemo(() => {
        const map = new Map<string, Image[]>();
        for (const img of images) {
            const label =
                typeof img.imageType === 'string' && img.imageType.trim()
                    ? img.imageType
                    : defaultGalleryLabel;
            if (!map.has(label)) map.set(label, []);
            map.get(label)!.push(img);
        }
        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([typeLabel, imgs]) => ({
                typeLabel,
                inspectionImageType: type,
                images: imgs,
            }));
    }, [images, type, defaultGalleryLabel]);

    const handleAddImage = async (
        event: ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>
    ) => {
        let files: FileList | null = null;

        if (event.type === 'change') {
            files = (event as ChangeEvent<HTMLInputElement>).target.files;
        } else if (event.type === 'drop') {
            event.preventDefault();
            files = (event as DragEvent<HTMLDivElement>).dataTransfer.files;
            setIsDragging(false);
        }

        if (files && files[0]) {
            for (const file of Array.from(files)) {
                const validation = await validateInspectionImageFile(file, { requirePortraitOrientation });
                if (!validation.accepted) {
                    if (validation.reason === 'landscape') {
                        toast.error(t('image_landscape_not_allowed'));
                    } else {
                        toast.error(interpolate(t('supported_images'), [supportedImageTypes.join(', ').replaceAll('image/', '')]));
                    }
                    continue;
                }
                const reader = new FileReader();
                reader.onloadend = async () => {
                    const fileData = (reader.result as string);
                    const uniqueId = generateUniqueId();  // Generate once and reuse
                    const img: FileDetails = {
                        id: uniqueId,
                        name: `${uniqueId}${getFileExtension(file)}`,
                        data: fileData,
                        contentType: file.type
                    };
                    onAdd(img);
                };
                reader.readAsDataURL(file);
            }
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };


    const handleDeleteImage = async (img: Image) => {
        onRemove(img);
    };

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        handleAddImage(event);
    };
    function addImageButton(listener: MouseEventHandler<HTMLDivElement>) {
        return (
            <div
                className={styles.buttonContainer}
                style={{ marginTop: images.length ? '1rem' : '0' }}
            >
                <div
                    className={images.length ? styles.addBtn : styles.cameraBtn} // Conditional class assignment
                    onClick={listener}
                >
                    {images.length ? (
                        <p>{t('Add') + ' +'}</p>
                    ) : (
                        <SvgIcon
                            component={FrameIcon}
                            fontSize="large"
                            sx={{
                                width: 100,
                                height: 45,
                                color: "white",
                            }}
                            viewBox="0 0 50 50"
                        />
                    )}
                </div>
            </div>
        );
    }



    return (
        <div
            className={styles.container}
        >
            <div>
                <div className={styles.heading}>{t('Images')}</div>
            </div>

            <hr className={styles.hr} />

            <div
                className={`${styles.dropArea} ${isDragging ? styles.dragging : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >


                {!!images.length && (
                    <div className={styles.imageGrid}>
                        {images.map((image, idx) => (
                            <ImageCard
                                type={type}
                                key={image.id}
                                inspectionId={inspectionId}
                                image={image}
                                onDelete={handleDeleteImage}
                                galleryPeers={images}
                                galleryIndex={idx}
                                sectionTypeLabel={gallerySectionLabel}
                                allInspectionImageGroups={allInspectionImageGroups}
                            />
                        ))}
                    </div>
                )}
                {addImageButton(triggerFileInput)}
                <input
                    hidden={true}
                    type="file"
                    // do not remove ["image/*", "text/plain"] its critical for taking image from camera
                    accept={[...supportedImageTypes, "image/*", "text/plain"].join(", ")}
                    onChange={handleAddImage}
                    className={styles.uploadInput}
                    ref={fileInputRef}
                />
                <p className={styles.supportedFormats}>{interpolate(t('supported_images'), [supportedImageTypes.join(', ').replaceAll('image/', '')])}</p>
            </div>
        </div >
    )
        ;
};
